// ===========================================
// TABLE AUDIO — Native Pixi Sound Engine (@pixi/sound)
// ===========================================
// Pixi-native audio wrapper for the game table.
// Uses @pixi/sound (WebAudio) instead of HTMLAudioElement.
// Single source of truth for volumes from animationRunner.ts.
// ===========================================

import { sound, Sound } from '@pixi/sound';
import { getSfxVolumeNormalized, getOutcomeVolumeNormalized } from '../../../../stores/tableSettingsStore';
import { BASE_VOLUMES } from '../../../../utils/animationRunner';
import { isAudioSuppressed } from './audioSuppression';

// Audio suppression (tab-visibility SFX guard) lives in ./audioSuppression to
// avoid a circular import with animationRunner.ts (BASE_VOLUMES). Re-exported
// here so existing importers of tableAudio keep working.
export { isAudioSuppressed };

// ===========================================
// SOUND ASSET IMPORTS (Vite provides URL strings)
// ===========================================

// Chip sounds (7 variations)
import chips1 from '../../../../assets/sfx/chips/chips1.wav';
import chips2 from '../../../../assets/sfx/chips/chips2.wav';
import chips3 from '../../../../assets/sfx/chips/chips3.wav';
import chips4 from '../../../../assets/sfx/chips/chips4.wav';
import chips5 from '../../../../assets/sfx/chips/chips5.wav';
import chips6 from '../../../../assets/sfx/chips/chips6.wav';
import chips7 from '../../../../assets/sfx/chips/chips7.wav';

// Deal sounds (2 variations)
import deal1 from '../../../../assets/sfx/cards/deal/deal1.wav';
import deal2 from '../../../../assets/sfx/cards/deal/deal2.wav';

// Rake sounds (2 variations)
import rake1 from '../../../../assets/sfx/cards/rake/rake1.wav';
import rake2 from '../../../../assets/sfx/cards/rake/rake2.wav';

// Split slide sound
import slideSfx from '../../../../assets/sfx/split/slide.wav';

// Outcome sounds (hand labels)
import winnerSfx from '../../../../assets/sfx/HANDLABEL/WINNER.wav';
import loserSfx from '../../../../assets/sfx/HANDLABEL/LOSER.wav';
import blackjackSfx from '../../../../assets/sfx/HANDLABEL/BLACKJACK.wav';
import bustedSfx from '../../../../assets/sfx/HANDLABEL/BUSTED.wav';
import pushSfx from '../../../../assets/sfx/HANDLABEL/PUSH.wav';

// ===========================================
// TYPES
// ===========================================

export type SoundKey =
  | 'chipLand'
  | 'deal'
  | 'rake'
  | 'slide'
  | 'outcomeWinner'
  | 'outcomeLoser'
  | 'outcomeBlackjack'
  | 'outcomeBusted'
  | 'outcomePush';

type SoundCategory = 'sfx' | 'outcome';

interface SoundEntry {
  pool: Sound[];
  category: SoundCategory;
  base: number;
}

// ===========================================
// SOUND REGISTRY
// ===========================================

// Map BASE_VOLUMES keys to our SoundKey format
const baseVolumeMap: Record<SoundKey, number> = {
  chipLand: BASE_VOLUMES.chip,
  deal: BASE_VOLUMES.deal,
  rake: BASE_VOLUMES.rake,
  slide: BASE_VOLUMES.slide,
  outcomeWinner: BASE_VOLUMES.winner,
  outcomeLoser: BASE_VOLUMES.loser,
  outcomeBlackjack: BASE_VOLUMES.blackjack,
  outcomeBusted: BASE_VOLUMES.busted,
  outcomePush: BASE_VOLUMES.push,
};

// Build Sound instances from URLs
function createSound(url: string): Sound {
  return Sound.from({
    url,
    preload: true,
  });
}

// Registry: key → { pool, category, base }
const registry: Record<SoundKey, SoundEntry> = {
  chipLand: {
    pool: [chips1, chips2, chips3, chips4, chips5, chips6, chips7].map(createSound),
    category: 'sfx',
    base: baseVolumeMap.chipLand,
  },
  deal: {
    pool: [deal1, deal2].map(createSound),
    category: 'sfx',
    base: baseVolumeMap.deal,
  },
  rake: {
    pool: [rake1, rake2].map(createSound),
    category: 'sfx',
    base: baseVolumeMap.rake,
  },
  slide: {
    pool: [slideSfx].map(createSound),
    category: 'sfx',
    base: baseVolumeMap.slide,
  },
  outcomeWinner: {
    pool: [winnerSfx].map(createSound),
    category: 'outcome',
    base: baseVolumeMap.outcomeWinner,
  },
  outcomeLoser: {
    pool: [loserSfx].map(createSound),
    category: 'outcome',
    base: baseVolumeMap.outcomeLoser,
  },
  outcomeBlackjack: {
    pool: [blackjackSfx].map(createSound),
    category: 'outcome',
    base: baseVolumeMap.outcomeBlackjack,
  },
  outcomeBusted: {
    pool: [bustedSfx].map(createSound),
    category: 'outcome',
    base: baseVolumeMap.outcomeBusted,
  },
  outcomePush: {
    pool: [pushSfx].map(createSound),
    category: 'outcome',
    base: baseVolumeMap.outcomePush,
  },
};

// ===========================================
// PUBLIC API
// ===========================================

export interface PlayTableSoundOptions {
  /** Volume multiplier 0.0-1.0 (default 1.0). Use 0.4 for opponent outcome sounds. */
  multiplier?: number;
}

/**
 * Play a table sound by key with optional volume multiplier.
 *
 * Volume formula: BASE[key] * categoryNorm * multiplier
 * - categoryNorm = getSfxVolumeNormalized() for SFX, getOutcomeVolumeNormalized() for outcomes
 * - multiplier defaults to 1.0; use 0.4 for opponent outcome sounds
 *
 * @pixi/sound handles polyphony automatically — overlapping sounds are allowed.
 *
 * @example
 * playTableSound('chipLand')                    // Full volume
 * playTableSound('outcomeWinner', { multiplier: 0.4 })  // Opponent ducking
 */
export function playTableSound(key: SoundKey, opts?: PlayTableSoundOptions): void {
  const entry = registry[key];
  if (!entry) return;

  // Drop sounds while suppressed (tab hidden or settle window) — prevents SFX burst on tab return
  if (isAudioSuppressed()) return;

  // Get category volume normalizer
  const norm = entry.category === 'outcome'
    ? getOutcomeVolumeNormalized()
    : getSfxVolumeNormalized();

  // Respect mute — no work when silent
  if (norm === 0) return;

  // Calculate final volume
  const multiplier = opts?.multiplier ?? 1;
  const volume = entry.base * norm * multiplier;

  // Pick a uniformly random Sound from the pool
  const poolIndex = Math.floor(Math.random() * entry.pool.length);
  const soundInstance = entry.pool[poolIndex];

  // Play with calculated volume — @pixi/sound spawns a fresh instance per play
  soundInstance.play({ volume });
}

// ===========================================
// OUTCOME STRING TO KEY MAPPING
// ===========================================

/**
 * Maps outcome string from game state to the appropriate SoundKey.
 *
 * Mapping:
 * - 'blackjack' → 'outcomeBlackjack'
 * - 'win' → 'outcomeWinner'
 * - 'bust' → 'outcomeBusted'
 * - 'lose' → 'outcomeLoser'
 * - 'push' → 'outcomePush'
 * - 'surrender' → 'outcomePush' (reuses PUSH sound, matching React stack)
 */
export function getOutcomeSoundKey(outcome: string): SoundKey | null {
  switch (outcome) {
    case 'blackjack':
      return 'outcomeBlackjack';
    case 'win':
      return 'outcomeWinner';
    case 'bust':
      return 'outcomeBusted';
    case 'lose':
      return 'outcomeLoser';
    case 'push':
    case 'surrender':
      return 'outcomePush';
    default:
      return null;
  }
}
